import { useState, useEffect } from "react";
import { login as apiLogin, getUsers, createUser, updateUser, deleteUser, activateUser, resetTraffic, resetSub, logout as apiLogout } from "./api";

// ─── ثابت‌ها ──────────────────────────────────────────────────────────────────
const statusColors = {
  active:  { bg: "#1a3a2a", text: "#4ade80", label: "فعال" },
  pending: { bg: "#1a2a3a", text: "#38bdf8", label: "در انتظار اتصال" },
  limited: { bg: "#3a2a1a", text: "#fb923c", label: "محدود" },
  disabled:{ bg: "#2a2a2a", text: "#9ca3af", label: "غیرفعال" },
  expired: { bg: "#3a1a1a", text: "#f87171", label: "منقضی" },
};

const APPS = [
  { platform:"اندروید", platformEn:"Android", icon:"🤖", key:"android", apps:[
    { name:"V2rayNG", platform:"ANDROID", descFa:"V2rayNG یک کلاینت V2Ray برای دستگاه‌های اندرویدی است.", descEn:"V2rayNG is a V2Ray client for Android devices.", recommended:true, hasAdd:true, icon:"V" },
    { name:"SingBox", platform:"ANDROID", descFa:"Sing-box یک کلاینت برای مسیریابی امن ترافیک فراهم می‌کند.", descEn:"Sing-box provides a client for secure traffic routing.", recommended:false, hasAdd:true, icon:"◈" },
  ]},
  { platform:"iOS", platformEn:"iOS", icon:"🍎", key:"ios", apps:[
    { name:"Streisand", platform:"IOS", descFa:"کلاینت پراکسی انعطاف‌پذیر با قوانین و DNS سفارشی.", descEn:"Flexible proxy client with rule support and custom DNS.", recommended:true, hasAdd:true, icon:"✦" },
    { name:"SingBox", platform:"IOS", descFa:"Sing-box یک کلاینت برای مسیریابی امن ترافیک فراهم می‌کند.", descEn:"Sing-box provides a client for secure traffic routing.", recommended:false, hasAdd:true, icon:"◈" },
    { name:"Shadowrocket", platform:"IOS", descFa:"Shadowrocket یک ابزار پروکسی قانون‌محور برای iOS است.", descEn:"Shadowrocket is a rule-based proxy tool for iOS.", recommended:false, hasAdd:false, icon:"🚀" },
  ]},
  { platform:"ویندوز", platformEn:"Windows", icon:"🪟", key:"windows", apps:[
    { name:"V2rayN", platform:"WINDOWS", descFa:"v2rayN یک کلاینت V2Ray برای ویندوز است.", descEn:"v2rayN is a V2Ray client for Windows.", recommended:true, hasAdd:false, icon:"V" },
    { name:"FlClash", platform:"WINDOWS", descFa:"Flclash یک کلاینت GUI چندسکویی برای clash core است.", descEn:"FlClash is a multi-platform GUI client for clash core.", recommended:false, hasAdd:false, icon:"⚡" },
  ]},
  { platform:"لینوکس", platformEn:"Linux", icon:"🐧", key:"linux", apps:[
    { name:"FlClash", platform:"LINUX", descFa:"Flclash یک کلاینت GUI چندسکویی برای clash core است.", descEn:"FlClash is a multi-platform GUI client for clash core.", recommended:true, hasAdd:false, icon:"⚡" },
    { name:"SingBox", platform:"LINUX", descFa:"Sing-box یک کلاینت برای مسیریابی امن ترافیک فراهم می‌کند.", descEn:"Sing-box provides a client for secure traffic routing.", recommended:false, hasAdd:true, icon:"◈" },
  ]},
];

const T = {
  fa: {
    dashboard:"داشبورد کاربری", expireWarning:(d)=>`سرویس شما در ${d} روز دیگر منقضی می‌شود.`,
    serviceVolume:"حجم سرویس", used:"حجم مصرفی", remaining:"باقی‌مانده", consumed:"مصرف شده",
    unlimited:"نامحدود", serviceInfo:"اطلاعات سرویس", expireDate:"تاریخ انقضا",
    daysLeft:"روزهای مانده", days:"روز", lastConn:"آخرین اتصال", protocol:"پروتکل",
    capacity:"ظرفیت", persons:"نفر", usageStats:"آمار مصرف", totalUsage:"مصرف کل",
    links:"لینک کانفیگ‌ها", subLink:"لینک اشتراک", copy:"📋 کپی", copied:"✅ کپی شد",
    apps:"اپلیکیشن‌ها", yourSystem:"سیستم شما", recommended:"پیشنهادی",
    add:"☁️ افزودن", download:"⬇️ دانلود",
    support:"پشتیبانی", supportDesc:"در صورت بروز مشکل با پشتیبانی تماس بگیرید",
    contactSupport:"📞 تماس با پشتیبانی", madeBy:"ساخته شده با ❤️ توسط",
    statusLabels:{ active:"فعال", pending:"در انتظار اتصال", limited:"محدود", disabled:"غیرفعال", expired:"منقضی" },
  },
  en: {
    dashboard:"User Dashboard", expireWarning:(d)=>`Your service expires in ${d} days.`,
    serviceVolume:"Total Volume", used:"Used", remaining:"Remaining", consumed:"Used",
    unlimited:"Unlimited", serviceInfo:"Service Info", expireDate:"Expire Date",
    daysLeft:"Days Left", days:"days", lastConn:"Last Connection", protocol:"Protocol",
    capacity:"Capacity", persons:"users", usageStats:"Usage Stats", totalUsage:"Total Usage",
    links:"Config Links", subLink:"Subscription Link", copy:"📋 Copy", copied:"✅ Copied",
    apps:"Applications", yourSystem:"Your System", recommended:"Recommended",
    add:"☁️ Add", download:"⬇️ Download",
    support:"Support", supportDesc:"Contact support if you have any issues",
    contactSupport:"📞 Contact Support", madeBy:"Made with ❤️ by",
    statusLabels:{ active:"Active", pending:"Waiting", limited:"Limited", disabled:"Disabled", expired:"Expired" },
  },
};

// ─── کامپوننت‌های کمکی ────────────────────────────────────────────────────────
function TrafficBar({ used, total, unlimited }) {
  const pct = unlimited ? 0 : Math.min((used/total)*100, 100);
  const color = unlimited?"#8b5cf6":pct>=90?"#f87171":pct>=60?"#fb923c":"#4ade80";
  return (
    <div style={{width:"100%",background:"#12141f",borderRadius:8,height:7,overflow:"hidden",boxShadow:"inset 0 1px 2px rgba(0,0,0,0.3)"}}>
      {unlimited
        ? <div style={{width:"100%",background:"linear-gradient(90deg,#6366f1,#8b5cf6)",height:"100%"}}/>
        : <div style={{width:`${pct}%`,background:color,height:"100%",borderRadius:8,transition:"width 0.5s",boxShadow:`0 0 8px ${color}66`}}/>
      }
    </div>
  );
}

// ─── صفحه لاگین ───────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true); setError("");
    const data = await apiLogin(username, password);
    if (data.token) { onLogin(); }
    else { setError(data.error || "خطا در ورود"); }
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",background:"radial-gradient(ellipse 900px 600px at 50% 0%,#1b2040 0%,#0f1120 55%,#0a0c18 100%)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:"rtl",padding:20}}>
      <div style={{background:"linear-gradient(180deg,#1e2030 0%,#191b2c 100%)",border:"1px solid #2a2e4d",borderRadius:24,padding:"40px 36px",width:340,boxShadow:"0 24px 60px -20px rgba(99,102,241,0.25), 0 8px 24px rgba(0,0,0,0.4)"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:64,height:64,margin:"0 auto 16px",borderRadius:20,background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,boxShadow:"0 10px 28px -8px rgba(139,92,246,0.55)"}}>⚡</div>
          <div style={{color:"#f1f0f7",fontWeight:800,fontSize:23,letterSpacing:"-0.3px"}}>GalaxyShop</div>
          <div style={{color:"#6b7280",fontSize:13,marginTop:5}}>پنل مدیریت VPN</div>
        </div>
        {error && <div style={{background:"rgba(248,113,113,0.1)",border:"1px solid rgba(248,113,113,0.25)",color:"#f87171",padding:"10px 14px",borderRadius:12,marginBottom:18,fontSize:13,textAlign:"center"}}>{error}</div>}
        <div style={{marginBottom:16}}>
          <div style={{color:"#8a8fa3",fontSize:12,marginBottom:6,fontWeight:600}}>نام کاربری</div>
          <input value={username} onChange={e=>setUsername(e.target.value)}
            style={{width:"100%",background:"#12141f",border:"1.5px solid #2a2e4d",borderRadius:12,padding:"12px 14px",color:"#f1f0f7",fontSize:14,boxSizing:"border-box",outline:"none",transition:"border-color .15s"}}
            onFocus={e=>e.target.style.borderColor="#8b5cf6"} onBlur={e=>e.target.style.borderColor="#2a2e4d"}/>
        </div>
        <div style={{marginBottom:26}}>
          <div style={{color:"#8a8fa3",fontSize:12,marginBottom:6,fontWeight:600}}>پسورد</div>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&handleLogin()}
            style={{width:"100%",background:"#12141f",border:"1.5px solid #2a2e4d",borderRadius:12,padding:"12px 14px",color:"#f1f0f7",fontSize:14,boxSizing:"border-box",outline:"none",transition:"border-color .15s"}}
            onFocus={e=>e.target.style.borderColor="#8b5cf6"} onBlur={e=>e.target.style.borderColor="#2a2e4d"}/>
        </div>
        <button onClick={handleLogin} disabled={loading}
          style={{width:"100%",padding:"13px 0",borderRadius:13,background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",border:"none",cursor:loading?"default":"pointer",fontWeight:700,fontSize:15,opacity:loading?0.7:1,boxShadow:"0 10px 24px -8px rgba(139,92,246,0.5)",transition:"transform .1s"}}
          onMouseDown={e=>!loading&&(e.currentTarget.style.transform="scale(0.98)")} onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}>
          {loading?"در حال ورود...":"ورود"}
        </button>
      </div>
    </div>
  );
}

// ─── صفحه ساب کاربر ──────────────────────────────────────────────────────────
function SubPage({ user, onBack }) {
  const [openPlatform, setOpenPlatform] = useState("android");
  const [copied, setCopied] = useState(null);
  const [lang, setLang] = useState("fa");
  const t = T[lang];
  const isRtl = lang==="fa";
  const isUnlimited = !!user.unlimited;
  const pct = isUnlimited ? 0 : Math.min(((user.traffic_used||0)/(user.traffic_limit||1))*100, 100);
  const remaining = isUnlimited ? "∞" : Math.max((user.traffic_limit||0)-(user.traffic_used||0), 0);
  const color = isUnlimited?"#8b5cf6":pct>=90?"#f87171":pct>=60?"#fb923c":"#4ade80";
  const sc = statusColors[user.status]||statusColors.active;
  const scLabel = t.statusLabels[user.status]||t.statusLabels.active;
  const DOMAIN = window.location.hostname;
  const subLink = `https://${DOMAIN}/sub/${user.sub_token}`;
  const vlessLink = `vless://${user.uuid}@${DOMAIN}:8443?type=tcp&security=tls&sni=${DOMAIN}#GalaxyShop-${user.username}`;
  const circumference = 2*Math.PI*54;
  const strokeDash = circumference-(pct/100)*circumference;

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(()=>{ setCopied(key); setTimeout(()=>setCopied(null),2000); });
  };

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0a0c1a 0%,#0f0a1e 40%,#0a1020 100%)",fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:isRtl?"rtl":"ltr",position:"relative",overflow:"hidden"}}>
      <div style={{position:"fixed",top:-80,right:-80,width:300,height:300,background:"radial-gradient(circle,#6366f122,transparent 70%)",borderRadius:"50%",pointerEvents:"none"}}/>
      <div style={{position:"fixed",bottom:100,left:-60,width:250,height:250,background:"radial-gradient(circle,#8b5cf618,transparent 70%)",borderRadius:"50%",pointerEvents:"none"}}/>
      {/* Header */}
      <div style={{background:"rgba(15,17,32,0.85)",backdropFilter:"blur(16px)",borderBottom:"1px solid rgba(99,102,241,0.2)",padding:"16px 20px",display:"flex",alignItems:"center",gap:12,position:"sticky",top:0,zIndex:10}}>
        <button onClick={onBack} style={{background:"rgba(139,92,246,0.15)",border:"1px solid #8b5cf633",color:"#8b5cf6",fontSize:18,cursor:"pointer",width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center"}}>
          {isRtl?"←":"→"}
        </button>
        <div style={{flex:1}}>
          <div style={{color:"#e2e8f0",fontWeight:700,fontSize:17}}>{t.dashboard}</div>
          <div style={{color:"#6b7280",fontSize:12}}>{user.username}</div>
        </div>
        <div style={{display:"flex",background:"rgba(21,23,40,0.8)",borderRadius:10,border:"1px solid #2d3050",overflow:"hidden"}}>
          {["fa","en"].map(l=>(
            <button key={l} onClick={()=>setLang(l)}
              style={{padding:"6px 12px",border:"none",cursor:"pointer",fontSize:13,fontWeight:700,background:lang===l?"linear-gradient(135deg,#6366f1,#8b5cf6)":"transparent",color:lang===l?"#fff":"#9ca3af"}}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <div style={{background:sc.bg,border:`1px solid ${sc.text}44`,borderRadius:20,padding:"4px 14px"}}>
          <span style={{color:sc.text,fontSize:13,fontWeight:600}}>{scLabel}</span>
        </div>
      </div>

      <div style={{padding:"20px 16px",maxWidth:500,margin:"0 auto"}}>
        {/* اعلان */}
        {user.expire_days<=7&&user.expire_days>0&&(
          <div style={{background:"#3a2a1a",border:"1px solid #fb923c44",borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:18}}>⚠️</span>
            <div style={{color:"#fb923c",fontSize:13}}>{t.expireWarning(user.expire_days)}</div>
          </div>
        )}

        {/* دایره مصرف */}
        <div style={{background:"linear-gradient(135deg,rgba(30,32,64,0.9),rgba(22,24,48,0.95))",border:"1px solid rgba(99,102,241,0.25)",borderRadius:24,padding:24,marginBottom:16,display:"flex",flexDirection:"column",alignItems:"center",backdropFilter:"blur(10px)",boxShadow:"0 8px 32px rgba(99,102,241,0.1)"}}>
          <div style={{position:"relative",marginBottom:20}}>
            {isUnlimited ? (
              <div style={{width:140,height:140,borderRadius:"50%",background:"conic-gradient(#8b5cf6,#6366f1,#06b6d4,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 0 30px #8b5cf644"}}>
                <div style={{width:118,height:118,borderRadius:"50%",background:"#0f1120",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                  <div style={{color:"#8b5cf6",fontWeight:900,fontSize:36}}>∞</div>
                  <div style={{color:"#6b7280",fontSize:10,marginTop:2}}>{t.unlimited}</div>
                </div>
              </div>
            ) : (
              <svg width="140" height="140" style={{transform:"rotate(-90deg)"}}>
                <circle cx="70" cy="70" r="54" fill="none" stroke="#2d3050" strokeWidth="10"/>
                <circle cx="70" cy="70" r="54" fill="none" stroke={color} strokeWidth="10"
                  strokeDasharray={circumference} strokeDashoffset={strokeDash}
                  strokeLinecap="round" style={{transition:"stroke-dashoffset 1s",filter:`drop-shadow(0 0 8px ${color}99)`}}/>
              </svg>
            )}
            {!isUnlimited&&(
              <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                <div style={{color,fontWeight:800,fontSize:26}}>{pct.toFixed(0)}%</div>
                <div style={{color:"#6b7280",fontSize:11}}>{t.consumed}</div>
              </div>
            )}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:0,width:"100%",background:"rgba(21,23,40,0.8)",borderRadius:14,overflow:"hidden",border:"1px solid rgba(45,48,80,0.5)"}}>
            {(isUnlimited?[
              [t.serviceVolume,"∞","#8b5cf6"],
              [t.used,`${user.traffic_used||0} GB`,"#60a5fa"],
              [t.remaining,"∞","#4ade80"],
            ]:[
              [t.serviceVolume,`${user.traffic_limit} GB`,"#e2e8f0"],
              [t.used,`${user.traffic_used||0} GB`,color],
              [t.remaining,typeof remaining==="number"?`${remaining.toFixed(1)} GB`:remaining,(typeof remaining==="number"&&remaining<5)?"#f87171":"#4ade80"],
            ]).map(([k,v,c],i)=>(
              <div key={k} style={{textAlign:"center",padding:"14px 8px",borderLeft:i>0?"1px solid #2d3050":"none"}}>
                <div style={{color:c,fontWeight:800,fontSize:i===0||isUnlimited?15:15}}>{v}</div>
                <div style={{color:"#6b7280",fontSize:10,marginTop:4}}>{k}</div>
              </div>
            ))}
          </div>
        </div>

        {/* اطلاعات */}
        <div style={{background:"rgba(30,32,48,0.85)",border:"1px solid rgba(45,48,80,0.6)",borderRadius:20,padding:20,marginBottom:16,backdropFilter:"blur(8px)"}}>
          <div style={{color:"#9ca3af",fontSize:12,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:6}}>
            <span>📋</span> {t.serviceInfo}
          </div>
          {[
            [t.expireDate, user.expires_at ? new Date(user.expires_at).toLocaleDateString("fa-IR") : "—"],
            [t.daysLeft, (()=>{ if(!user.expires_at) return (user.expire_days===0 && user.expire_hours>0) ? `${user.expire_hours} ساعت` : `${user.expire_days||0} ${t.days}`; const diff = new Date(user.expires_at).getTime() - Date.now(); if(diff<=0) return "منقضی شده"; const h = diff/(1000*60*60); return h<24 ? `${Math.ceil(h)} ساعت` : `${Math.ceil(h/24)} ${t.days}`; })()],
            [t.protocol, user.protocol?.toUpperCase()],
            [t.capacity, `${user.active_connections||0} / ${user.capacity||1} ${t.persons}`],
          ].map(([k,v])=>(
            <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #1a1c2e"}}>
              <span style={{color:"#6b7280",fontSize:13}}>{k}</span>
              <span style={{color:"#e2e8f0",fontSize:13,fontWeight:600}}>{v}</span>
            </div>
          ))}
        </div>

        {/* لینک‌ها */}
        <div style={{background:"rgba(30,32,48,0.85)",border:"1px solid rgba(45,48,80,0.6)",borderRadius:20,padding:20,marginBottom:16,backdropFilter:"blur(8px)"}}>
          <div style={{color:"#e2e8f0",fontWeight:700,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
            <span>🔗</span> {t.links}
          </div>
          {[
            {label:lang==="fa"?"لینک ساب":t.subLink, value:subLink, key:"sub"},
            {label:"VLESS", value:vlessLink, key:"vless"},
          ].map(item=>(
            <div key={item.key} style={{background:"#151728",borderRadius:12,padding:"12px 14px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{color:"#9ca3af",fontSize:12,fontWeight:600}}>{item.label}</div>
                <button onClick={()=>handleCopy(item.value,item.key)}
                  style={{background:copied===item.key?"#1a3a2a":"#2d3050",border:"none",color:copied===item.key?"#4ade80":"#9ca3af",borderRadius:8,padding:"5px 12px",cursor:"pointer",fontSize:11,whiteSpace:"nowrap"}}>
                  {copied===item.key?t.copied:t.copy}
                </button>
              </div>
              <div style={{color:"#6b7280",fontSize:10,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* اپ‌ها */}
        <div style={{background:"rgba(30,32,48,0.85)",border:"1px solid rgba(45,48,80,0.6)",borderRadius:20,overflow:"hidden",marginBottom:16,backdropFilter:"blur(8px)"}}>
          <div style={{padding:"16px 20px",borderBottom:"1px solid #2d3050"}}>
            <div style={{color:"#e2e8f0",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
              <span>📱</span> {t.apps}
            </div>
          </div>
          {APPS.map((section,si)=>(
            <div key={section.key}>
              <button onClick={()=>setOpenPlatform(openPlatform===section.key?null:section.key)}
                style={{width:"100%",background:openPlatform===section.key?"#252840":"transparent",border:"none",borderBottom:"1px solid #2d3050",padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18}}>{section.icon}</span>
                  <span style={{color:"#e2e8f0",fontWeight:600,fontSize:15}}>{lang==="fa"?section.platform:section.platformEn}</span>
                  {section.key==="android"&&<span style={{background:"#6366f1",color:"#fff",fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600}}>{t.yourSystem}</span>}
                </div>
                <span style={{color:"#6b7280",fontSize:16,transform:openPlatform===section.key?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.3s"}}>∨</span>
              </button>
              {openPlatform===section.key&&(
                <div style={{padding:"12px 16px",background:"#181a2e",borderBottom:si<APPS.length-1?"1px solid #2d3050":"none"}}>
                  {section.apps.map((app,i)=>(
                    <div key={app.name} style={{background:"#1e2030",border:"1px solid #2d3050",borderRadius:14,padding:"14px 16px",marginBottom:i<section.apps.length-1?10:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:40,height:40,background:"linear-gradient(135deg,#2d3050,#1a1c2e)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,border:"1px solid #3d4070"}}>
                            {app.icon}
                          </div>
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{color:"#e2e8f0",fontWeight:700,fontSize:15}}>{app.name}</span>
                              {app.recommended&&<span style={{background:"#1a2a4a",color:"#60a5fa",fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600,border:"1px solid #60a5fa33"}}>{t.recommended}</span>}
                            </div>
                            <div style={{color:"#6b7280",fontSize:11,marginTop:2}}>{app.platform}</div>
                          </div>
                        </div>
                      </div>
                      <div style={{color:"#9ca3af",fontSize:12,lineHeight:1.6,marginBottom:12}}>{lang==="fa"?app.descFa:app.descEn}</div>
                      <div style={{display:"flex",gap:8}}>
                        {app.hasAdd&&(
                          <button onClick={()=>navigator.clipboard.writeText(subLink)}
                            style={{flex:1,padding:"8px 0",borderRadius:10,background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",border:"none",cursor:"pointer",fontSize:12,fontWeight:600}}>
                            {t.add}
                          </button>
                        )}
                        <button style={{flex:1,padding:"8px 0",borderRadius:10,background:"#151728",color:"#9ca3af",border:"1px solid #2d3050",cursor:"pointer",fontSize:12}}>
                          {t.download}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* پشتیبانی */}
        <div style={{background:"rgba(30,32,48,0.85)",border:"1px solid rgba(45,48,80,0.6)",borderRadius:20,padding:20,marginBottom:20,textAlign:"center",backdropFilter:"blur(8px)"}}>
          <div style={{fontSize:28,marginBottom:8}}>🎧</div>
          <div style={{color:"#e2e8f0",fontWeight:700,marginBottom:6}}>{t.support}</div>
          <div style={{color:"#6b7280",fontSize:13,marginBottom:14}}>{t.supportDesc}</div>
          <button style={{background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",border:"none",borderRadius:10,padding:"10px 28px",cursor:"pointer",fontWeight:700,fontSize:13}}>
            {t.contactSupport}
          </button>
        </div>

        <div style={{textAlign:"center",color:"#4b5563",fontSize:12,paddingBottom:20}}>
          {t.madeBy} <span style={{color:"#8b5cf6",fontWeight:600}}>GalaxyShop</span> Team
        </div>
      </div>
    </div>
  );
}

// ─── Modal افزودن کاربر ───────────────────────────────────────────────────────
function AddUserModal({ onClose, onAdd }) {
  const [form, setForm] = useState({ username:"", protocol:"vless", capacity:"1", unlimited:false, server_type:"reality" });
  const [timeUnit, setTimeUnit] = useState("day");
  const [timeValue, setTimeValue] = useState(30);
  const [trafficUnit, setTrafficUnit] = useState("GB");
  const [trafficValue, setTrafficValue] = useState(10);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    const trafficGB = trafficUnit==="MB" ? trafficValue/1024 : trafficValue;
    await onAdd({
      username: form.username,
      protocol: form.protocol,
      capacity: form.capacity==="∞" ? 999999 : parseInt(form.capacity)||1,
      unlimited: form.unlimited,
      traffic_limit: trafficGB,
      expire_days: timeUnit==="day" ? timeValue : 0,
      expire_hours: timeUnit==="hour" ? timeValue : 0,
      server_type: form.server_type || "reality",
    });
    setLoading(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(8,9,16,0.72)",backdropFilter:"blur(4px)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#1e2030",border:"1px solid #2d3050",borderRadius:18,padding:24,width:"92%",maxWidth:360,maxHeight:"90vh",overflowY:"auto",direction:"rtl",fontFamily:"'Vazirmatn',Tahoma,sans-serif"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{color:"#e2e8f0",fontWeight:700,fontSize:16}}>➕ کاربر جدید</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#9ca3af",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        <div style={{marginBottom:14}}>
          <div style={{color:"#9ca3af",fontSize:12,marginBottom:5}}>🌐 نوع سرور</div>
          <div style={{display:"flex",gap:8}}>
            {[{v:"reality",label:"🔒 Reality",color:"#8b5cf6"},{v:"reality_vip",label:"🌟 VIP",color:"#facc15"},{v:"cdn",label:"☁️ CDN",color:"#38bdf8"},{v:"tunnel",label:"🔗 Tunnel",color:"#4ade80"}].map(opt=>(
              <button key={opt.v} onClick={()=>setForm({...form,server_type:opt.v})}
                style={{flex:1,padding:"8px 0",borderRadius:8,background:form.server_type===opt.v?"#1a1a3a":"#151728",color:form.server_type===opt.v?opt.color:"#9ca3af",border:"1px solid "+(form.server_type===opt.v?opt.color:"#2d3050"),cursor:"pointer",fontSize:12,fontWeight:600}}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{color:"#9ca3af",fontSize:12,marginBottom:5}}>👤 نام کاربری</div>
          <input value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="مثلاً user_ali"
            style={{width:"100%",background:"#151728",border:"1px solid #2d3050",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:14,boxSizing:"border-box"}}/>
        </div>

        <div style={{marginBottom:14}}>
          <div style={{color:"#9ca3af",fontSize:12,marginBottom:8,display:"flex",justifyContent:"space-between"}}>
            <span>⏱ مدت زمان</span>
            <div style={{display:"flex",background:"#151728",borderRadius:8,overflow:"hidden",border:"1px solid #2d3050"}}>
              {["hour","day"].map(u=>(
                <button key={u} onClick={()=>{setTimeUnit(u);setTimeValue(u==="hour"?1:30);}}
                  style={{padding:"3px 10px",border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:timeUnit===u?"linear-gradient(135deg,#6366f1,#8b5cf6)":"transparent",color:timeUnit===u?"#fff":"#9ca3af"}}>
                  {u==="hour"?"ساعت":"روز"}
                </button>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
            {(timeUnit==="hour"?[1,3,6,12]:[1,7,30,90]).map(v=>(
              <button key={v} onClick={()=>setTimeValue(v)}
                style={{padding:"5px 12px",borderRadius:8,background:timeValue===v?(timeUnit==="hour"?"#1a2a4a":"#1a3a2a"):"#151728",color:timeValue===v?(timeUnit==="hour"?"#60a5fa":"#4ade80"):"#9ca3af",border:`1px solid ${timeValue===v?(timeUnit==="hour"?"#60a5fa":"#4ade80"):"#2d3050"}`,cursor:"pointer",fontSize:12}}>
                {v} {timeUnit==="hour"?"ساعت":"روز"}
              </button>
            ))}
          </div>
          <input type="number" value={timeValue} min="1" onChange={e=>setTimeValue(parseInt(e.target.value)||1)}
            style={{width:"100%",background:"#151728",border:"1px solid #2d3050",borderRadius:8,padding:"8px 12px",color:"#e2e8f0",fontSize:13,boxSizing:"border-box"}}/>
        </div>

        <div style={{marginBottom:14}}>
          <div style={{color:"#9ca3af",fontSize:12,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>📦 حجم ترافیک</span>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{display:"flex",background:"#151728",borderRadius:8,overflow:"hidden",border:"1px solid #2d3050"}}>
                {["MB","GB"].map(u=>(
                  <button key={u} onClick={()=>{setTrafficUnit(u);setTrafficValue(u==="MB"?100:10);}}
                    style={{padding:"3px 10px",border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:trafficUnit===u?"linear-gradient(135deg,#6366f1,#8b5cf6)":"transparent",color:trafficUnit===u?"#fff":"#9ca3af"}}>
                    {u}
                  </button>
                ))}
              </div>
              <div onClick={()=>setForm({...form,unlimited:!form.unlimited})} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}>
                <div style={{width:32,height:18,borderRadius:9,background:form.unlimited?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#2d3050",position:"relative",transition:"background 0.3s"}}>
                  <div style={{position:"absolute",top:1,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.3s",left:form.unlimited?16:2}}/>
                </div>
                <span style={{color:form.unlimited?"#8b5cf6":"#6b7280",fontSize:10,fontWeight:600}}>∞</span>
              </div>
            </div>
          </div>
          {!form.unlimited&&(
            <>
              <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
                {(trafficUnit==="MB"?[100,200,500]:[1,5,10,50,100]).map(v=>(
                  <button key={v} onClick={()=>setTrafficValue(v)}
                    style={{padding:"5px 10px",borderRadius:8,background:trafficValue===v?"#3a2a1a":"#151728",color:trafficValue===v?"#fb923c":"#9ca3af",border:`1px solid ${trafficValue===v?"#fb923c":"#2d3050"}`,cursor:"pointer",fontSize:12}}>
                    {v} {trafficUnit}
                  </button>
                ))}
              </div>
              <input type="number" value={trafficValue} min="1" onChange={e=>setTrafficValue(parseInt(e.target.value)||1)}
                style={{width:"100%",background:"#151728",border:"1px solid #2d3050",borderRadius:8,padding:"8px 12px",color:"#e2e8f0",fontSize:13,boxSizing:"border-box"}}/>
            </>
          )}
          {form.unlimited&&<div style={{background:"#151728",borderRadius:8,padding:"10px 12px",textAlign:"center",color:"#8b5cf6",fontWeight:700,fontSize:18}}>∞ نامحدود</div>}
        </div>

        <div style={{marginBottom:14}}>
          <div style={{color:"#9ca3af",fontSize:12,marginBottom:5}}>👥 ظرفیت</div>
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            {["∞",1,2,3,5,10].map(v=>(
              <button key={v} onClick={()=>setForm({...form,capacity:String(v)})}
                style={{flex:1,padding:"5px 0",borderRadius:8,background:form.capacity===String(v)?"#1a2a4a":"#151728",color:form.capacity===String(v)?"#60a5fa":"#9ca3af",border:`1px solid ${form.capacity===String(v)?"#60a5fa":"#2d3050"}`,cursor:"pointer",fontSize:12}}>
                {v}
              </button>
            ))}
          </div>
        </div>

        <div style={{marginBottom:20}}>
          <div style={{color:"#9ca3af",fontSize:12,marginBottom:5}}>پروتکل</div>
          <div style={{display:"flex",gap:6}}>
            {["vless","vmess","trojan"].map(p=>(
              <button key={p} onClick={()=>setForm({...form,protocol:p})}
                style={{flex:1,padding:"8px 0",borderRadius:8,background:form.protocol===p?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#151728",color:form.protocol===p?"#fff":"#9ca3af",border:`1px solid ${form.protocol===p?"#6366f1":"#2d3050"}`,cursor:"pointer",fontSize:12,fontWeight:form.protocol===p?700:400}}>
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div style={{background:"#151728",borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",justifyContent:"space-between"}}>
          <span style={{color:"#6b7280",fontSize:12}}>خلاصه:</span>
          <span style={{color:"#e2e8f0",fontSize:12,fontWeight:600}}>
            {form.unlimited?"∞":`${trafficValue} ${trafficUnit}`} • {timeValue} {timeUnit==="hour"?"ساعت":"روز"} • {form.capacity} نفر
          </span>
        </div>

        <button onClick={handleSubmit} disabled={loading}
          style={{width:"100%",padding:"12px 0",borderRadius:10,background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",border:"none",cursor:"pointer",fontWeight:700,fontSize:14}}>
          {loading?"...":"✅ ساخت کاربر"}
        </button>
      </div>
    </div>
  );
}

// ─── Modal مدیریت کاربر ──────────────────────────────────────────────────────
function UserModal({ user, onClose, onAction, onSubPage }) {
  const [renewDays, setRenewDays] = useState(30);
  const [addGB, setAddGB] = useState(10);
  const [newCap, setNewCap] = useState(user.capacity||1);
  const [loading, setLoading] = useState("");
  const isPending = user.status==="pending";
  const sc = statusColors[user.status]||statusColors.active;

  const handle = async (action) => {
    setLoading(action);
    await onAction(action, user);
    setLoading("");
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(8,9,16,0.72)",backdropFilter:"blur(4px)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#1e2030",border:"1px solid #2d3050",borderRadius:18,padding:24,width:"92%",maxWidth:400,maxHeight:"90vh",overflowY:"auto",direction:"rtl",fontFamily:"'Vazirmatn',Tahoma,sans-serif"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{color:"#e2e8f0",fontWeight:700,fontSize:16}}>👤 {user.username}</span>
            <span style={{background:sc.bg,color:sc.text,padding:"2px 10px",borderRadius:20,fontSize:11}}>{sc.label}</span>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#9ca3af",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {isPending&&(
          <div style={{background:"#0f1e2e",border:"1px solid #38bdf844",borderRadius:12,padding:14,marginBottom:14,textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:6}}>⏳</div>
            <div style={{color:"#38bdf8",fontWeight:700,fontSize:14,marginBottom:4}}>در انتظار اولین اتصال</div>
            <div style={{color:"#6b7280",fontSize:12,marginBottom:12}}>تایمر و حجم هنوز شروع نشده</div>
            <button onClick={()=>handle("activate")} disabled={!!loading}
              style={{width:"100%",padding:"10px 0",borderRadius:10,background:"linear-gradient(135deg,#0ea5e9,#38bdf8)",color:"#fff",border:"none",cursor:"pointer",fontWeight:700,fontSize:13}}>
              {loading==="activate"?"...":"▶ فعال‌سازی دستی"}
            </button>
          </div>
        )}

        {/* تمدید */}
        <div style={{background:"#151728",borderRadius:12,padding:14,marginBottom:12,opacity:isPending?0.5:1}}>
          <div style={{color:"#4ade80",fontSize:13,fontWeight:600,marginBottom:10}}>📅 تمدید سرویس</div>
          <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
            {[7,15,30,60,90].map(d=>(
              <button key={d} onClick={()=>!isPending&&setRenewDays(d)}
                style={{padding:"4px 10px",borderRadius:8,background:renewDays===d?"#1a3a2a":"#1e2030",color:renewDays===d?"#4ade80":"#9ca3af",border:`1px solid ${renewDays===d?"#4ade80":"#2d3050"}`,cursor:isPending?"not-allowed":"pointer",fontSize:12}}>
                {d} روز
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="number" value={renewDays} onChange={e=>!isPending&&setRenewDays(parseInt(e.target.value)||1)}
              style={{width:60,background:"#1e2030",border:"1px solid #2d3050",borderRadius:8,padding:"6px 8px",color:"#e2e8f0",fontSize:13,textAlign:"center"}}/>
            <span style={{color:"#6b7280",fontSize:12}}>روز</span>
            <button onClick={()=>!isPending&&handle(`renew-${renewDays}`)} disabled={!!loading||isPending}
              style={{flex:1,padding:"8px 0",borderRadius:8,background:"#1a3a2a",color:"#4ade80",border:"none",cursor:isPending?"not-allowed":"pointer",fontSize:13,fontWeight:600}}>
              {loading.startsWith("renew")?"...":"✅ تمدید"}
            </button>
          </div>
        </div>

        {/* افزایش حجم */}
        <div style={{background:"#151728",borderRadius:12,padding:14,marginBottom:12,opacity:isPending?0.5:1}}>
          <div style={{color:"#fb923c",fontSize:13,fontWeight:600,marginBottom:10}}>📦 افزایش حجم</div>
          <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
            {[5,10,20,50,100].map(g=>(
              <button key={g} onClick={()=>!isPending&&setAddGB(g)}
                style={{padding:"4px 10px",borderRadius:8,background:addGB===g?"#3a2a1a":"#1e2030",color:addGB===g?"#fb923c":"#9ca3af",border:`1px solid ${addGB===g?"#fb923c":"#2d3050"}`,cursor:isPending?"not-allowed":"pointer",fontSize:12}}>
                +{g} GB
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="number" value={addGB} onChange={e=>!isPending&&setAddGB(parseInt(e.target.value)||1)}
              style={{width:60,background:"#1e2030",border:"1px solid #2d3050",borderRadius:8,padding:"6px 8px",color:"#e2e8f0",fontSize:13,textAlign:"center"}}/>
            <span style={{color:"#6b7280",fontSize:12}}>GB</span>
            <button onClick={()=>!isPending&&handle(`addgb-${addGB}`)} disabled={!!loading||isPending}
              style={{flex:1,padding:"8px 0",borderRadius:8,background:"#3a2a1a",color:"#fb923c",border:"none",cursor:isPending?"not-allowed":"pointer",fontSize:13,fontWeight:600}}>
              {loading.startsWith("addgb")?"...":"➕ اضافه"}
            </button>
          </div>
        </div>

        {/* افزایش ظرفیت */}
        <div style={{background:"#151728",borderRadius:12,padding:14,marginBottom:12,opacity:isPending?0.5:1}}>
          <div style={{color:"#38bdf8",fontSize:13,fontWeight:600,marginBottom:10}}>👥 ظرفیت کاربران</div>
          <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
            {[1,2,3,5,10].map(c=>(
              <button key={c} onClick={()=>!isPending&&handle(`setcap-${c}`)} disabled={!!loading||isPending}
                style={{padding:"4px 10px",borderRadius:8,background:user.capacity===c?"#1a2a3a":"#1e2030",color:user.capacity===c?"#38bdf8":"#9ca3af",border:`1px solid ${user.capacity===c?"#38bdf8":"#2d3050"}`,cursor:isPending?"not-allowed":"pointer",fontSize:12}}>
                {c} نفر
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="number" min="1" defaultValue={newCap} onChange={e=>setNewCap(parseInt(e.target.value)||1)}
              style={{width:60,background:"#1e2030",border:"1px solid #2d3050",borderRadius:8,padding:"6px 8px",color:"#e2e8f0",fontSize:13,textAlign:"center"}}/>
            <span style={{color:"#6b7280",fontSize:12}}>نفر</span>
            <button onClick={()=>!isPending&&handle(`setcap-${newCap}`)} disabled={!!loading||isPending}
              style={{flex:1,padding:"8px 0",borderRadius:8,background:"#1a2a3a",color:"#38bdf8",border:"none",cursor:isPending?"not-allowed":"pointer",fontSize:13,fontWeight:600}}>
              {loading.startsWith("setcap")?"...":"✅ تغییر ظرفیت"}
            </button>
          </div>
        </div>
        <div style={{background:"#151728",borderRadius:12,padding:14,marginBottom:12}}>
          <div style={{color:"#a78bfa",fontSize:13,fontWeight:600,marginBottom:8}}>🔗 تغییر لینک ساب</div>
          <div style={{color:"#6b7280",fontSize:11,marginBottom:10}}>لینک ساب + UUID جدید — حجم و روز دست نخورده</div>
          <button onClick={()=>handle("resetsub")} disabled={!!loading}
            style={{width:"100%",padding:"9px 0",borderRadius:8,background:"linear-gradient(135deg,#4c1d95,#7c3aed)",color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:600}}>
            {loading==="resetsub"?"...":"🔄 تولید لینک ساب جدید"}
          </button>
        </div>

        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <button onClick={()=>handle("reset")} disabled={!!loading}
            style={{flex:1,padding:"8px 0",borderRadius:8,background:"#1a2a4a",color:"#60a5fa",border:"none",cursor:"pointer",fontSize:13}}>
            {loading==="reset"?"...":"🔄 ریست ترافیک"}
          </button>
          <button onClick={()=>handle("delete")} disabled={!!loading}
            style={{flex:1,padding:"8px 0",borderRadius:8,background:"#2a1a1a",color:"#f87171",border:"none",cursor:"pointer",fontSize:13}}>
            {loading==="delete"?"...":"🗑 حذف"}
          </button>
        </div>
        {!isPending&&(
          <button onClick={()=>handle("toggle")} disabled={!!loading}
            style={{width:"100%",padding:"10px 0",borderRadius:8,background:user.status==="disabled"?"#1a3a2a":"#2a2a2a",color:user.status==="disabled"?"#4ade80":"#9ca3af",border:"none",cursor:"pointer",fontSize:13}}>
            {loading==="toggle"?"...":user.status==="disabled"?"✅ فعال‌سازی":"⛔ غیرفعال‌سازی"}
          </button>
        )}
        <button onClick={()=>{onClose();onSubPage(user);}} style={{width:"100%",padding:"10px 0",borderRadius:8,background:"#1a1a3a",color:"#8b5cf6",border:"1px solid #8b5cf633",cursor:"pointer",fontSize:13,marginTop:8}}>
          🔗 مشاهده صفحه ساب
        </button>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [subPageUser, setSubPageUser] = useState(null);

  const showToast = (msg, type="success") => {
    setToast({msg,type});
    setTimeout(()=>setToast(null), 2500);
  };

  const fetchUsers = async () => {
    setLoading(true);
    const data = await getUsers();
    if (Array.isArray(data)) { setUsers(data); setSelectedUser(prev => prev ? data.find(u => u.id === prev.id) || prev : null); }
    setLoading(false);
  };

  useEffect(()=>{ if(isLoggedIn) fetchUsers(); },[isLoggedIn]);

  const handleAdd = async (userData) => {
    const res = await createUser(userData);
    if (res.success) {
      showToast(`کاربر ${userData.username} ساخته شد`);
      setShowAdd(false);
      fetchUsers();
    } else {
      showToast(res.error||"خطا","error");
    }
  };

  const handleAction = async (action, user) => {
    if (action==="delete") {
      await deleteUser(user.id);
      showToast(`${user.username} حذف شد`,"error");
      setSelectedUser(null); fetchUsers();
    } else if (action==="reset") {
      await resetTraffic(user.id);
      showToast("ترافیک ریست شد"); fetchUsers();
    } else if (action==="resetsub") {
      await resetSub(user.id);
      showToast("لینک ساب جدید صادر شد ✅"); fetchUsers();
    } else if (action==="activate") {
      await activateUser(user.id);
      showToast("سرویس فعال شد ✅");
      setSelectedUser(null); fetchUsers();
    } else if (action==="toggle") {
      await updateUser(user.id,{status:user.status==="disabled"?"active":"disabled"});
      showToast("وضعیت تغییر کرد");
      setSelectedUser(null); fetchUsers();
    } else if (action.startsWith("renew-")) {
      const days = parseInt(action.slice(action.indexOf("-")+1));
      await updateUser(user.id,{expire_days:(user.expire_days||0)+days});
      showToast(`${days} روز تمدید شد ✅`); fetchUsers();
    } else if (action.startsWith("addgb-")) {
      const gb = parseInt(action.slice(action.indexOf("-")+1));
      await updateUser(user.id,{traffic_limit:(user.traffic_limit||0)+gb});
      showToast(`${gb} GB اضافه شد ✅`); fetchUsers();
    } else if (action.startsWith("setcap-")) {
      const cap = parseInt(action.split("-")[1]);
      await updateUser(user.id,{capacity:cap});
      showToast(`ظرفیت به ${cap} نفر تغییر کرد ✅`); fetchUsers();
    }
  };

  if (!isLoggedIn) return <LoginPage onLogin={()=>setIsLoggedIn(true)}/>;
  if (subPageUser) return <SubPage user={subPageUser} onBack={()=>setSubPageUser(null)}/>;

  const filtered = users.filter(u=>u.username?.includes(search) && (tab==="reality" ? (u.server_type==="reality" || !u.server_type) : tab==="reality_vip" ? u.server_type==="reality_vip" : tab==="cdn" ? u.server_type==="cdn" : tab==="tunnel" ? u.server_type==="tunnel" : true));
  const activeCount = users.filter(u=>u.status==="active").length;
  const pendingCount = users.filter(u=>u.status==="pending").length;
  const totalTraffic = users.reduce((s,u)=>s+(u.traffic_used||0),0);

  return (
    <div style={{minHeight:"100vh",background:"radial-gradient(ellipse 1200px 700px at 20% 0%,#171c33 0%,#0f1120 45%)",fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:"rtl",display:"flex"}}>
      {toast&&(
        <div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",background:toast.type==="error"?"#3a1a1a":"#1a3a2a",border:`1px solid ${toast.type==="error"?"#f87171":"#4ade80"}`,color:toast.type==="error"?"#f87171":"#4ade80",padding:"10px 24px",borderRadius:10,zIndex:200,fontSize:14,fontWeight:600,whiteSpace:"nowrap"}}>
          {toast.msg}
        </div>
      )}

      {/* Sidebar */}
      <div style={{width:210,background:"#13152400",backdropFilter:"blur(0px)",borderLeft:"1px solid #23264080",padding:"26px 0",display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"0 20px 22px",borderBottom:"1px solid #23264080",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:9}}>
            <div style={{width:34,height:34,borderRadius:10,background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,boxShadow:"0 6px 16px -4px rgba(139,92,246,0.5)"}}>⚡</div>
            <div>
              <div style={{color:"#f1f0f7",fontWeight:800,fontSize:16,lineHeight:1.1}}>GalaxyShop</div>
              <div style={{color:"#6b7280",fontSize:10.5,marginTop:2}}>پنل VPN</div>
            </div>
          </div>
        </div>
        {[
          {key:"dashboard",icon:"📊",label:"داشبورد"},
          {key:"reality",icon:"🔒",label:"کاربران Reality"},
          {key:"reality_vip",icon:"🌟",label:"کاربران VIP Reality"},
          {key:"cdn",icon:"🌐",label:"کاربران CDN"},
          {key:"tunnel",icon:"🔗",label:"کاربران Tunnel"},
          {key:"users",icon:"👥",label:"کاربران"},
        ].map(item=>(
          <button key={item.key} onClick={()=>setTab(item.key)}
            style={{display:"flex",alignItems:"center",gap:11,margin:"2px 10px",padding:"11px 12px",borderRadius:11,background:tab===item.key?"linear-gradient(90deg,rgba(139,92,246,0.16),rgba(139,92,246,0.03))":"none",border:"none",color:tab===item.key?"#a78bfa":"#8a8fa3",cursor:"pointer",fontSize:13,fontWeight:tab===item.key?700:500,textAlign:"right",width:"calc(100% - 20px)",transition:"background .15s,color .15s"}}
            onMouseEnter={e=>{if(tab!==item.key)e.currentTarget.style.background="#1c1f36"}}
            onMouseLeave={e=>{if(tab!==item.key)e.currentTarget.style.background="none"}}>
            <span style={{fontSize:15}}>{item.icon}</span>{item.label}
          </button>
        ))}
        <div style={{marginTop:"auto",padding:"16px 20px 2px",borderTop:"1px solid #23264080"}}>
          <div style={{color:"#4ade80",fontSize:11,display:"flex",alignItems:"center",gap:7,marginBottom:10,fontWeight:600}}>
            <span style={{position:"relative",width:7,height:7,background:"#4ade80",borderRadius:"50%",display:"inline-block",boxShadow:"0 0 0 3px rgba(74,222,128,0.18)"}}/>
            سرور آنلاین
          </div>
          <button onClick={()=>{apiLogout();setIsLoggedIn(false);}} style={{background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.18)",borderRadius:9,color:"#f87171",cursor:"pointer",fontSize:12,padding:"8px 0",width:"100%",textAlign:"center",fontWeight:600}}>
            🚪 خروج
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{flex:1,padding:"24px 20px",overflow:"auto"}}>
        {tab==="dashboard"&&(
          <>
            <h1 style={{color:"#f1f0f7",margin:"0 0 26px",fontSize:22,fontWeight:800,letterSpacing:"-0.3px"}}>داشبورد</h1>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:14,marginBottom:24}}>
              {[
                {label:"کل کاربران",value:users.length,icon:"👥",color:"#818cf8"},
                {label:"فعال",value:activeCount,icon:"✅",color:"#4ade80"},
                {label:"در انتظار",value:pendingCount,icon:"⏳",color:"#38bdf8"},
                {label:"ترافیک مصرفی",value:`${totalTraffic.toFixed(1)} GB`,icon:"📡",color:"#fb923c"},
              ].map(s=>(
                <div key={s.label} style={{background:"linear-gradient(180deg,#1c1f36 0%,#181a2c 100%)",border:"1px solid #262a48",borderRadius:16,padding:20,transition:"transform .15s,box-shadow .15s",cursor:"default"}}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 12px 28px -10px rgba(0,0,0,0.5)"}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none"}}>
                  <div style={{width:38,height:38,borderRadius:11,background:s.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,marginBottom:14}}>{s.icon}</div>
                  <div style={{color:s.color,fontSize:23,fontWeight:800,letterSpacing:"-0.5px"}}>{s.value}</div>
                  <div style={{color:"#6b7280",fontSize:11.5,marginTop:4,fontWeight:500}}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{background:"linear-gradient(180deg,#1c1f36 0%,#181a2c 100%)",border:"1px solid #262a48",borderRadius:16,padding:20}}>
                <div style={{color:"#8a8fa3",fontSize:12.5,marginBottom:14,fontWeight:700}}>📊 وضعیت کاربران</div>
                {Object.entries(statusColors).map(([s,c])=>(
                  <div key={s} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #23264080"}}>
                    <span style={{background:c.bg,color:c.text,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600}}>{c.label}</span>
                    <span style={{color:"#e2e8f0",fontWeight:700}}>{users.filter(u=>u.status===s).length}</span>
                  </div>
                ))}
              </div>
              <div style={{background:"linear-gradient(180deg,#1c1f36 0%,#181a2c 100%)",border:"1px solid #262a48",borderRadius:16,padding:20}}>
                <div style={{color:"#8a8fa3",fontSize:12.5,marginBottom:14,fontWeight:700}}>📡 ترافیک</div>
                <div style={{color:"#fb923c",fontSize:30,fontWeight:800,letterSpacing:"-0.5px"}}>{totalTraffic.toFixed(1)}</div>
                <div style={{color:"#6b7280",fontSize:11.5}}>GB مصرف کل</div>
                <div style={{marginTop:14,display:"inline-flex",alignItems:"center",gap:6,background:"rgba(74,222,128,0.1)",color:"#4ade80",fontSize:12.5,fontWeight:700,padding:"5px 12px",borderRadius:20}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:"#4ade80"}}/>{activeCount} کاربر فعال
                </div>
              </div>
            </div>
          </>
        )}

        {(tab==="users" || tab==="reality" || tab==="reality_vip" || tab==="cdn" || tab==="tunnel")&&(
          <>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <h1 style={{color:"#f1f0f7",margin:0,fontSize:22,fontWeight:800,letterSpacing:"-0.3px"}}>کاربران</h1>
              <button onClick={()=>setShowAdd(true)}
                style={{background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",border:"none",borderRadius:12,padding:"10px 20px",cursor:"pointer",fontWeight:700,fontSize:13,boxShadow:"0 8px 20px -6px rgba(139,92,246,0.5)",transition:"transform .1s"}}
                onMouseDown={e=>e.currentTarget.style.transform="scale(0.97)"} onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}>
                ➕ کاربر جدید
              </button>
            </div>
            <div style={{position:"relative",marginBottom:16}}>
              <span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:14,opacity:0.6}}>🔍</span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="جستجوی کاربر..."
                style={{width:"100%",background:"#181a2c",border:"1.5px solid #262a48",borderRadius:12,padding:"11px 38px 11px 14px",color:"#f1f0f7",fontSize:13,boxSizing:"border-box",outline:"none",transition:"border-color .15s"}}
                onFocus={e=>e.target.style.borderColor="#8b5cf6"} onBlur={e=>e.target.style.borderColor="#262a48"}/>
            </div>
            {loading ? (
              <div style={{textAlign:"center",color:"#6b7280",padding:40}}>در حال بارگذاری...</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:11}}>
                {filtered.map(user=>{
                  const sc = statusColors[user.status]||statusColors.active;
                  const pct = user.unlimited?0:Math.min(((user.traffic_used||0)/(user.traffic_limit||1))*100,100);
                  const isFull = (user.active_connections||0)>=(user.capacity||1);
                  const isVip = user.server_type==="reality_vip";
                  return (
                    <div key={user.id} style={{background:"linear-gradient(180deg,#1c1f36 0%,#181a2c 100%)",border:`1px solid ${isFull?"#5a2a5a":isVip?"#4a3f10":"#262a48"}`,borderRadius:16,padding:"16px 18px",transition:"border-color .15s"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                        <div style={{display:"flex",alignItems:"center",gap:11}}>
                          <div style={{width:38,height:38,background:isVip?"linear-gradient(135deg,#facc15,#f59e0b)":"linear-gradient(135deg,#6366f1,#8b5cf6)",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:14,flexShrink:0}}>
                            {user.username?.[0]?.toUpperCase()||"U"}
                          </div>
                          <div>
                            <div style={{color:"#f1f0f7",fontWeight:700,fontSize:13.5,display:"flex",alignItems:"center",gap:6}}>
                              {user.username}
                              {user.unlimited?<span style={{background:"rgba(139,92,246,0.16)",color:"#a78bfa",fontSize:10,padding:"2px 7px",borderRadius:8,fontWeight:700}}>∞</span>:null}
                              {isVip?<span style={{background:"rgba(250,204,21,0.14)",color:"#facc15",fontSize:10,padding:"2px 7px",borderRadius:8,fontWeight:700}}>🌟 VIP</span>:null}
                            </div>
                            <div style={{color:"#6b7280",fontSize:11,marginTop:2}}>{user.protocol?.toUpperCase()} · {user.expire_days} روز</div>
                          </div>
                        </div>
                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                          <span style={{background:isFull?"rgba(232,121,249,0.12)":"rgba(96,165,250,0.12)",color:isFull?"#e879f9":"#60a5fa",padding:"3px 9px",borderRadius:20,fontSize:10.5,fontWeight:600}}>
                            👥 {user.active_connections||0}/{user.capacity||1}
                          </span>
                          <span style={{background:sc.bg,color:sc.text,padding:"3px 9px",borderRadius:20,fontSize:10.5,fontWeight:600}}>{sc.label}</span>
                        </div>
                      </div>
                      <TrafficBar used={user.traffic_used||0} total={user.traffic_limit||1} unlimited={!!user.unlimited}/>
                      <div style={{display:"flex",justifyContent:"space-between",margin:"6px 0 12px"}}>
                        {user.unlimited
                          ? <span style={{color:"#a78bfa",fontSize:11,fontWeight:600}}>∞ نامحدود · {user.traffic_used||0} GB مصرف</span>
                          : <><span style={{color:"#8a8fa3",fontSize:11}}>{user.traffic_used||0} GB مصرف</span><span style={{color:"#8a8fa3",fontSize:11}}>{pct.toFixed(0)}% از {user.traffic_limit} GB</span></>
                        }
                      </div>
                      <div style={{display:"flex",gap:7}}>
                        <button onClick={()=>setSelectedUser(user)} style={{flex:1,padding:"8px 0",borderRadius:9,background:"#262a48",color:"#e2e8f0",border:"none",cursor:"pointer",fontSize:11.5,fontWeight:600}}>✏️ مدیریت</button>
                        {user.status==="pending"
                          ? <button onClick={()=>handleAction("activate",user)} style={{flex:2,padding:"8px 0",borderRadius:9,background:"linear-gradient(135deg,#0ea5e9,#38bdf8)",color:"#fff",border:"none",cursor:"pointer",fontSize:11.5,fontWeight:700}}>▶ فعال‌سازی</button>
                          : <>
                              <button onClick={()=>handleAction("renew-30",user)} style={{flex:1,padding:"8px 0",borderRadius:9,background:"rgba(74,222,128,0.1)",color:"#4ade80",border:"none",cursor:"pointer",fontSize:11.5,fontWeight:600}}>📅 +30 روز</button>
                              <button onClick={()=>handleAction("addgb-10",user)} style={{flex:1,padding:"8px 0",borderRadius:9,background:"rgba(251,146,60,0.1)",color:"#fb923c",border:"none",cursor:"pointer",fontSize:11.5,fontWeight:600}}>📦 +10 GB</button>
                            </>
                        }
                        <button onClick={()=>setSubPageUser(user)} style={{flex:1,padding:"8px 0",borderRadius:9,background:"rgba(139,92,246,0.1)",color:"#a78bfa",border:"none",cursor:"pointer",fontSize:11.5,fontWeight:600}}>🔗 ساب</button>
                      </div>
                    </div>
                  );
                })}
                {filtered.length===0&&!loading&&(
                  <div style={{textAlign:"center",color:"#6b7280",padding:56}}>
                    <div style={{fontSize:34,marginBottom:10,opacity:.6}}>🔍</div>
                    هیچ کاربری پیدا نشد
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {selectedUser&&<UserModal user={selectedUser} onClose={()=>setSelectedUser(null)} onAction={handleAction} onSubPage={setSubPageUser}/>}
      {showAdd&&<AddUserModal onClose={()=>setShowAdd(false)} onAdd={handleAdd}/>}
    </div>
  );
}
